import React, { useState, useMemo, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { EditablePill } from "@/components/common/EditablePill";

// Generic pill type that can be extended
export interface SearchPill {
  type: string;
  value: string;
  id: string;
  isNew?: boolean;
}

// Filter configuration for different filter types
export interface FilterConfig {
  type: string;
  symbol: string; // e.g., "@", "#"
  label: string; // e.g., "Status", "Skill"
  icon: React.ReactNode;
  options: string[];
  placeholder?: string;
}

// Props for the SearchBar component
export interface SearchBarProps {
  placeholder?: string;
  filters?: FilterConfig[];
  pills: SearchPill[];
  onPillsChange: (pills: SearchPill[]) => void;
  onSearch?: (searchTerm: string) => void;
  className?: string;
  disabled?: boolean;
  renderFilterOption?: (option: string) => React.ReactNode;
  renderPillIcon?: (type: string, value: string) => React.ReactNode;
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

// PillsContainer component for displaying filter pills
const PillsContainer = memo(function PillsContainer({
  pills,
  onRemovePill,
  removingPillIds,
  newPillIds,
  getPillIcon,
}: {
  pills: SearchPill[];
  onRemovePill: (idx: number) => void;
  removingPillIds: Set<string>;
  newPillIds: Set<string>;
  getPillIcon: (type: string, value: string) => React.ReactNode;
}) {
  const memoizedPills = useMemo(() => pills, [pills]);

  return (
    <div className="flex flex-col gap-2">
      <motion.div
        className="flex gap-2 mt-4 overflow-x-auto pb-2 no-scrollbar"
        layout
        transition={{
          layout: {
            duration: 0.3,
            delay: 0.2,
          },
        }}
      >
        <AnimatePresence mode="popLayout">
          {memoizedPills.map((pill, idx) => (
            <motion.span
              key={pill.id}
              layout
              initial={
                newPillIds.has(pill.id) ? { opacity: 0, scale: 0.8 } : false
              }
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.8,
                transition: { duration: 0.3 },
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
                mass: 1,
              }}
              style={{ transformOrigin: "center" }}
              className="flex-shrink-0"
            >
              <EditablePill
                text={pill.value}
                icon={getPillIcon(pill.type, pill.value)}
                onIconChange={() => {}}
                onColorChange={() => {}}
                isActive={false}
                disableIconEdit={true}
                showRemoveButton={true}
                onRemove={() => onRemovePill(idx)}
                isNew={newPillIds.has(pill.id)}
              />
            </motion.span>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
});

// SearchInput component
const SearchInput = memo(function SearchInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-slate-900 dark:bg-slate-800/50 dark:text-slate-200 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-700 focus:ring-indigo-400 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
});

// FilterDropdown component for showing available filter options
const FilterDropdown = memo(function FilterDropdown({
  visibleOptions,
  highlightedIndex,
  onSelect,
  onMouseEnterIndex,
  filterLabel,
  renderOption,
}: {
  visibleOptions: string[];
  highlightedIndex: number;
  onSelect: (option: string) => void;
  onMouseEnterIndex?: (idx: number) => void;
  filterLabel: string;
  renderOption?: (option: string) => React.ReactNode;
}) {
  if (!visibleOptions.length) return null;

  return (
    <div className="absolute w-96 left-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-lg z-50 overflow-y-auto max-h-60">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-3 py-1">
        {filterLabel} Options
      </div>
      {visibleOptions.map((option, idx) => (
        <button
          key={option}
          type="button"
          onClick={() => onSelect(option)}
          onMouseEnter={
            onMouseEnterIndex ? () => onMouseEnterIndex(idx) : undefined
          }
          className={`block w-full text-left px-3 py-2 border-0 bg-transparent rounded-xl transition-colors
            hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200
            ${
              highlightedIndex === idx
                ? "bg-indigo-400! text-white dark:bg-indigo-800 dark:text-indigo-200"
                : ""
            }`}
        >
          {renderOption ? renderOption(option) : option}
        </button>
      ))}
    </div>
  );
});

export const SearchBar = memo(function SearchBar({
  placeholder = "Search...",
  filters = [],
  pills,
  onPillsChange,
  onSearch,
  className = "",
  disabled = false,
  renderFilterOption,
  renderPillIcon,
}: SearchBarProps) {
  // Local state for search input and filter mode
  const [searchInput, setSearchInput] = useState("");
  const [filterMode, setFilterMode] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [highlightedFilterIndex, setHighlightedFilterIndex] = useState(-1);
  const [removingPillIds, setRemovingPillIds] = useState<Set<string>>(
    new Set()
  );
  const [newPillIds, setNewPillIds] = useState<Set<string>>(new Set());

  // Get current filter config
  const currentFilterConfig = useMemo(() => {
    return filters.find((f) => f.type === filterMode);
  }, [filters, filterMode]);

  // Get available filter options based on current filter mode
  const getAvailableFilterOptions = useCallback(
    (mode: string) => {
      const config = filters.find((f) => f.type === mode);
      return config?.options || [];
    },
    [filters]
  );

  // Get filtered options based on search
  const getFilteredOptions = useCallback(
    (mode: string) => {
      const options = getAvailableFilterOptions(mode);
      if (!filterSearch) return options;

      const searchLower = filterSearch.toLowerCase();
      return options.filter((option) =>
        option.toLowerCase().includes(searchLower)
      );
    },
    [getAvailableFilterOptions, filterSearch]
  );

  // Get visible options (limit to 8 for dropdown)
  const getVisibleOptions = useCallback(
    (mode: string) => {
      const filtered = getFilteredOptions(mode);
      return filtered.slice(0, 8);
    },
    [getFilteredOptions]
  );

  // Get filtered visible options (exclude already selected)
  const getFilteredVisibleOptions = useCallback(
    (mode: string) => {
      const visible = getVisibleOptions(mode);
      const selectedValues = pills
        .filter((p) => p.type === mode)
        .map((p) => p.value);
      return visible.filter((option) => !selectedValues.includes(option));
    },
    [getVisibleOptions, pills]
  );

  // Handle adding a filter pill
  const handleAddFilterPill = useCallback(
    (type: string, value: string) => {
      const newId = `${type}-${value}-${Date.now()}`;
      const newPills = [...pills];
      if (!newPills.some((p) => p.type === type && p.value === value)) {
        newPills.push({ type, value, id: newId });
        onPillsChange(newPills);
        setNewPillIds((prev) => new Set(prev).add(newId));
        setTimeout(() => {
          setNewPillIds((prev) => {
            const next = new Set(prev);
            next.delete(newId);
            return next;
          });
        }, 300);
      }
    },
    [pills, onPillsChange]
  );

  // Handle adding a search term pill
  const handleAddSearchTermPill = useCallback(
    (term: string) => {
      const newId = `search-${term}-${Date.now()}`;
      const newPills = [...pills];
      if (!newPills.some((p) => p.type === "search" && p.value === term)) {
        newPills.push({ type: "search", value: term, id: newId });
        onPillsChange(newPills);
        setNewPillIds((prev) => new Set(prev).add(newId));
        setTimeout(() => {
          setNewPillIds((prev) => {
            const next = new Set(prev);
            next.delete(newId);
            return next;
          });
        }, 300);
      }
    },
    [pills, onPillsChange]
  );

  // Handle removing a pill
  const handleRemovePill = useCallback(
    (idx: number) => {
      const pillToRemove = pills[idx];
      setRemovingPillIds((prev) => new Set(prev).add(pillToRemove.id));

      setTimeout(() => {
        const newPills = pills.filter((_, i) => i !== idx);
        onPillsChange(newPills);
        setRemovingPillIds((prev) => {
          const next = new Set(prev);
          next.delete(pillToRemove.id);
          return next;
        });
      }, 300);
    },
    [pills, onPillsChange]
  );

  // Handle smart filter selection
  const handleSmartFilterSelect = useCallback(
    (type: string, value: string) => {
      if (!pills.some((p) => p.type === type && p.value === value)) {
        handleAddFilterPill(type, value);
        setSearchInput("");
        setFilterMode(null);
        setFilterSearch("");
        setHighlightedFilterIndex(-1);
      }
    },
    [handleAddFilterPill, pills]
  );

  // Handle search input change
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);

      // Get the last character typed
      const lastChar = value.slice(-1);

      // Check for filter activation by symbol
      if (lastChar && value.length === 1) {
        // Check if last character matches any filter symbol
        const matchingFilter = filters.find((f) => f.symbol === lastChar);
        if (matchingFilter) {
          setFilterMode(matchingFilter.type);
          setFilterSearch("");
          setHighlightedFilterIndex(0);
        } else {
          setFilterMode(null);
          setFilterSearch("");
        }
      } else if (value.length > 1) {
        // For when we have text after the symbol
        const symbolPatterns = filters.map((f) => ({
          filter: f,
          pattern: new RegExp(`^${escapeRegExp(f.symbol)}([\\w\\s-]+)$`),
        }));

        const matchingPattern = symbolPatterns.find(({ pattern }) =>
          pattern.test(value)
        );

        if (matchingPattern) {
          const match = value.match(matchingPattern.pattern);
          if (match) {
            setFilterMode(matchingPattern.filter.type);
            setFilterSearch(match ? match[1] : "");
          } else {
            setFilterMode(null);
            setFilterSearch("");
          }
        } else {
          setFilterMode(null);
          setFilterSearch("");
        }
      } else {
        setFilterMode(null);
        setFilterSearch("");
      }

      // Always show dropdown when in filter mode
      if (filterMode) {
        setHighlightedFilterIndex(0);
      }
    },
    [pills, onPillsChange, onSearch, filters]
  );

  // Handle search input keydown
  const handleSearchInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (filterMode) {
        const visibleOptions = getFilteredVisibleOptions(filterMode);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightedFilterIndex((prev) => {
            if (visibleOptions.length === 0) return -1;
            return prev === visibleOptions.length - 1 ? 0 : prev + 1;
          });
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedFilterIndex((prev) => {
            if (visibleOptions.length === 0) return -1;
            return prev <= 0 ? visibleOptions.length - 1 : prev - 1;
          });
        } else if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();

          if (
            highlightedFilterIndex >= 0 &&
            visibleOptions[highlightedFilterIndex]
          ) {
            handleSmartFilterSelect(
              filterMode,
              visibleOptions[highlightedFilterIndex]
            );
          } else if (
            filterSearch &&
            !pills.some(
              (p) => p.type === filterMode && p.value === filterSearch
            )
          ) {
            handleSmartFilterSelect(filterMode, filterSearch);
          }
        }
      } else if (e.key === "Enter" && searchInput.trim() !== "") {
        e.preventDefault();
        const term = searchInput.trim();
        if (
          term &&
          !pills.some((p) => p.type === "search" && p.value === term)
        ) {
          handleAddSearchTermPill(term);
        }
        setSearchInput("");
        setFilterMode(null);
        setFilterSearch("");
        setHighlightedFilterIndex(-1);
      }
    },
    [
      filterMode,
      getFilteredVisibleOptions,
      highlightedFilterIndex,
      handleSmartFilterSelect,
      searchInput,
      pills,
      handleAddSearchTermPill,
      filterSearch,
    ]
  );

  // Get pill icon based on type
  const getPillIcon = useCallback(
    (type: string, value: string) => {
      if (renderPillIcon) {
        return renderPillIcon(type, value);
      }
      if (type === "search") {
        return <FontAwesomeIcon icon={faSearch} />;
      }
      const filterConfig = filters.find((f) => f.type === type);
      return (
        filterConfig?.icon || (
          <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
        )
      );
    },
    [filters, renderPillIcon]
  );

  // Build placeholder text with filter hints
  const buildPlaceholder = useCallback(() => {
    if (placeholder) return placeholder;

    const filterHints = filters.map((f) => `${f.symbol}${f.type}`).join(" or ");
    return `Search${filterHints ? `, ${filterHints}` : ""}...`;
  }, [placeholder, filters]);

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="flex items-center gap-2">
        <SearchInput
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchInputKeyDown}
          placeholder={buildPlaceholder()}
          disabled={disabled}
        />
      </div>

      {/* Filter Dropdown */}
      {filterMode && currentFilterConfig && (
        <FilterDropdown
          visibleOptions={getFilteredVisibleOptions(filterMode)}
          highlightedIndex={highlightedFilterIndex}
          onSelect={(option) => handleSmartFilterSelect(filterMode, option)}
          onMouseEnterIndex={setHighlightedFilterIndex}
          filterLabel={currentFilterConfig.label}
          renderOption={renderFilterOption}
        />
      )}

      {/* Pills Container */}
      {pills.length > 0 && (
        <PillsContainer
          pills={pills}
          onRemovePill={handleRemovePill}
          removingPillIds={removingPillIds}
          newPillIds={newPillIds}
          getPillIcon={getPillIcon}
        />
      )}
    </div>
  );
});

export default SearchBar;
